import { State, Action, StateContext } from '@ngxs/store';
import { CustomerEquipmentUpgradeAgreement } from '../../models/equipment-upgrade-agreement.model';
import { Injectable, inject } from '@angular/core';
import {
  ApiGetEquipmentUpgradeAgreementsForCustomerAction,
  ApiGetEquipmentUpgradeAgreementsForCustomerSuccessAction,
  ApiGetEquipmentUpgradeAgreementsForCustomerErrorAction,
  ApiRefreshCustomerEquipmentsAction,
  ApiRefreshCustomerEquipmentsSuccessAction,
  ApiRefreshCustomerEquipmentsErrorAction,
  ApiValidateAgreementImeiAction,
  ApiValidateAgreementImeiSuccessAction,
  ApiValidateAgreementImeiErrorAction,
  SetMobileInsuranceAndReturnDetailsAction,
  SetMobileDamageDetailsAction,
  SetClaimIdOnEquipmentUpgradeAgreementAction,
  ResetRedemptionDetailsAction,
  SetMobileBytteOfferingSelected,
  ResetMobilBytteState,
  SetRedemptionIdAction,
  RegisterMobilBytteBytteAction,
  SetReturnDetailsAction,
  ResetSelectedMobilBytteOffering,
} from './mobilbytte.actions';
import { tap, catchError, take, finalize } from 'rxjs/operators';
import { NotificationService } from '@core/services/notification.service';
import { EquipmentService } from '@sales/services/equipment.service';
import { patch, updateItem } from '@ngxs/store/operators';
import { CustomerService } from '@sales/modules/details/services/customer.service';
import { HardwareOfferingWithMobilbytteDTO } from '../../models/mobilephone-variant';
import { EquipmentServiceV2 } from '@app/sales/services/equipment-v2.service';

export interface MobilBytteStateModel {
  existingEquipmentUpgradeAgreements: CustomerEquipmentUpgradeAgreement[];
  isLoadingEquipmentUpgradeAgreements: boolean;
  isLoadingSendEquipmentUpgradeSms: boolean;
  isLoadingMobilBytteBytteRegistration: boolean;
  isRefreshingEquipments: boolean;
  hasRefreshedEquipments: boolean;
  mobilBytteOfferingSelected: HardwareOfferingWithMobilbytteDTO;
}

enum Messages {
  EquipmentUpgradeError = 'Feil ved henting av MobilBytte-avtaler',
  SendingEquipmentUpgradeSmsError = 'Feil ved sending av MobilBytte contract sms',
  ErrorOnImeiValidation = 'Feil ved validering av imei',
  ErrorOnRedemption = 'Feil ved opprettelse av forsikring',
  ErrorOnRefreshEquipments = 'Feil ved oppdatering av mobiltelefoner',
}

const initialState = {
  existingEquipmentUpgradeAgreements: [],
  isLoadingEquipmentUpgradeAgreements: false,
  isLoadingSendEquipmentUpgradeSms: false,
  isLoadingMobilBytteBytteRegistration: false,
  isRefreshingEquipments: false,
  hasRefreshedEquipments: false,
  mobilBytteOfferingSelected: null,
};

@State<MobilBytteStateModel>({
  name: 'mobilbytte',
  defaults: initialState,
})
@Injectable()
export class MobilBytteState {
  private customerService = inject(CustomerService);
  private equipmentService = inject(EquipmentService);
  private equipmentServiceV2 = inject(EquipmentServiceV2);
  private notificationService = inject(NotificationService);

  @Action(ApiGetEquipmentUpgradeAgreementsForCustomerAction)
  public getEquipmentUpgradeAgreements(
    { dispatch, patchState }: StateContext<MobilBytteStateModel>,
    { customerId }: ApiGetEquipmentUpgradeAgreementsForCustomerAction
  ) {
    patchState({ isLoadingEquipmentUpgradeAgreements: true });

    return this.customerService.getMobilBytteAgreements(customerId).pipe(
      tap(response => dispatch(new ApiGetEquipmentUpgradeAgreementsForCustomerSuccessAction(response))),
      catchError(() => dispatch(new ApiGetEquipmentUpgradeAgreementsForCustomerErrorAction(Messages.EquipmentUpgradeError)))
    );
  }

  @Action(ApiGetEquipmentUpgradeAgreementsForCustomerSuccessAction)
  public getEquipmentUpgradeAgreementsSuccess(
    { patchState }: StateContext<MobilBytteStateModel>,
    { existingEquipmentUpgradeAgreements }: ApiGetEquipmentUpgradeAgreementsForCustomerSuccessAction
  ) {
    patchState({
      isLoadingEquipmentUpgradeAgreements: false,
      existingEquipmentUpgradeAgreements,
    });
  }

  @Action(ApiGetEquipmentUpgradeAgreementsForCustomerErrorAction)
  public getEquipmentUpgradeAgreementsError(
    { patchState }: StateContext<MobilBytteStateModel>,
    { error }: ApiGetEquipmentUpgradeAgreementsForCustomerErrorAction
  ) {
    patchState({ isLoadingEquipmentUpgradeAgreements: false });
    this.notificationService.error(error);
  }

  @Action(ApiRefreshCustomerEquipmentsAction)
  public apiRefreshCustomerEquipments(
    { dispatch, patchState }: StateContext<MobilBytteStateModel>,
    { customerId }: ApiRefreshCustomerEquipmentsAction
  ) {
    patchState({ isRefreshingEquipments: true, isLoadingEquipmentUpgradeAgreements: true });
    return this.equipmentService.refreshEquipments(customerId).pipe(
      tap(() => dispatch(new ApiRefreshCustomerEquipmentsSuccessAction(customerId))),
      catchError(() => dispatch(new ApiRefreshCustomerEquipmentsErrorAction()))
    );
  }

  @Action(ApiRefreshCustomerEquipmentsSuccessAction)
  public apiRefreshCustomerEquipmentsSuccess(
    { dispatch, patchState }: StateContext<MobilBytteStateModel>,
    { customerId }: ApiRefreshCustomerEquipmentsSuccessAction
  ) {
    patchState({ isRefreshingEquipments: false, hasRefreshedEquipments: true });
    return dispatch(new ApiGetEquipmentUpgradeAgreementsForCustomerAction(customerId));
  }

  @Action(ApiRefreshCustomerEquipmentsErrorAction)
  public apiRefreshCustomerEquipmentsError({ patchState }: StateContext<MobilBytteStateModel>) {
    patchState({ isRefreshingEquipments: false, hasRefreshedEquipments: false, isLoadingEquipmentUpgradeAgreements: false });
    this.notificationService.error(Messages.ErrorOnRefreshEquipments);
  }

  @Action(ApiValidateAgreementImeiAction)
  public apiValidateAgreementImei(
    { dispatch, getState, setState }: StateContext<MobilBytteStateModel>,
    { agreementId }: ApiValidateAgreementImeiAction
  ) {
    const agreement = getState().existingEquipmentUpgradeAgreements.find(a => a.agreementId === agreementId);
    if (agreement) {
      setState(
        patch({
          existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
            p => p.agreementId === agreementId,
            patch<CustomerEquipmentUpgradeAgreement>({ isValidatingImei: true })
          ),
        })
      );
      if (agreement.productVersion === 'V1') {
        return this.equipmentService.validateImei(agreement.agreementId, agreement.activeImei).pipe(
          tap(result => dispatch(new ApiValidateAgreementImeiSuccessAction(agreement.agreementId, result))),
          catchError(() =>
            dispatch(new ApiValidateAgreementImeiErrorAction(agreement.agreementId, Messages.ErrorOnImeiValidation))
          )
        );
      } else {
        return this.equipmentServiceV2.validateImei(agreement.agreementId, agreement.activeImei).pipe(
          tap(result => dispatch(new ApiValidateAgreementImeiSuccessAction(agreement.agreementId, result))),
          catchError(() =>
            dispatch(new ApiValidateAgreementImeiErrorAction(agreement.agreementId, Messages.ErrorOnImeiValidation))
          )
        );
      }
    }
  }

  @Action(ApiValidateAgreementImeiSuccessAction)
  public apiValidateAgreementImeiSuccess(
    { setState, getState }: StateContext<MobilBytteStateModel>,
    { agreementId, isValidImei }: ApiValidateAgreementImeiSuccessAction
  ) {
    const agreement = getState().existingEquipmentUpgradeAgreements.find(a => a.agreementId === agreementId);
    if (!isValidImei) {
      this.notificationService.error(`Kan ikke bytte telefon med IMEI: ${agreement.activeImei}`);
    }
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            isValidatingImei: false,
            isValidImei: isValidImei,
            hasCompletedImeiValidation: true,
          })
        ),
      })
    );
  }

  @Action(ApiValidateAgreementImeiErrorAction)
  public apiValidateAgreementImeiError(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, error }: ApiValidateAgreementImeiErrorAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            isValidatingImei: false,
            isValidImei: false,
            hasCompletedImeiValidation: true,
          })
        ),
      })
    );
    this.notificationService.error(error);
  }

  @Action(SetMobileInsuranceAndReturnDetailsAction)
  public apiSendGradingAnswersSuccess(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, redemptionId, insuranceEligibilityResponse, mobileReturnMethod }: SetMobileInsuranceAndReturnDetailsAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            insuranceEligibility: insuranceEligibilityResponse,
            mobileReturnMethod,
            redemptionId,
          })
        ),
      })
    );
  }

  @Action(SetClaimIdOnEquipmentUpgradeAgreementAction)
  public setClaimIdOnEquipmentUpgradeAgreement(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, claimId }: SetClaimIdOnEquipmentUpgradeAgreementAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            claimId,
          })
        ),
      })
    );
  }

  @Action(SetRedemptionIdAction)
  public setRedemptionId(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, redemptionId }: SetRedemptionIdAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => +p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            redemptionId,
          })
        ),
      })
    );

    return redemptionId;
  }

  @Action(SetReturnDetailsAction)
  public setReturnDetails(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, returnDetails }: SetReturnDetailsAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => +p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            mobileReturnMethod: returnDetails,
          })
        ),
      })
    );
  }

  @Action(ResetRedemptionDetailsAction)
  public resetRedemption({ setState }: StateContext<MobilBytteStateModel>, { agreementId }: ResetRedemptionDetailsAction) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            redemptionId: null,
            claimId: null,
            insuranceEligibility: null,
            damageDetails: null,
            mobileReturnMethod: null,
          })
        ),
      })
    );
  }

  @Action(SetMobileDamageDetailsAction)
  public setMobileDamageDetails(
    { setState }: StateContext<MobilBytteStateModel>,
    { agreementId, damageDetails }: SetMobileDamageDetailsAction
  ) {
    setState(
      patch({
        existingEquipmentUpgradeAgreements: updateItem<CustomerEquipmentUpgradeAgreement>(
          p => p.agreementId === agreementId,
          patch<CustomerEquipmentUpgradeAgreement>({
            damageDetails: damageDetails,
          })
        ),
      })
    );
  }

  @Action(ResetMobilBytteState)
  public resetMobilBytteState({ setState }: StateContext<MobilBytteStateModel>) {
    setState({ ...initialState });
  }

  @Action(SetMobileBytteOfferingSelected)
  public setMobilBytteOfferingSelected(
    { patchState }: StateContext<MobilBytteStateModel>,
    { mobilBytteOfferingSelected }: SetMobileBytteOfferingSelected
  ) {
    patchState({ mobilBytteOfferingSelected });
  }

  @Action(ResetSelectedMobilBytteOffering)
  public resetSelectedMobilBytteOffering({ patchState }: StateContext<MobilBytteStateModel>) {
    patchState({ mobilBytteOfferingSelected: null });
  }

  @Action(RegisterMobilBytteBytteAction)
  public registerMobilBytteBytte(
    { dispatch, setState }: StateContext<MobilBytteStateModel>,
    { request, mobilBytteAgreementId }: RegisterMobilBytteBytteAction
  ) {
    setState(patch({ isLoadingMobilBytteBytteRegistration: true }));

    return this.equipmentServiceV2.registerMobilbytteBytte(request).pipe(
      take(1),
      tap(response => {
        dispatch(new SetRedemptionIdAction(mobilBytteAgreementId, response.redemptionCreated.redemptionId));
      }),
      catchError(error => {
        if (error?.error?.status === 409) {
          this.notificationService.error(
            'Det finnes allerede en åpen bytteprosess for denne avtalen. Kontakt kundeservice.'
          );
        } else {
          this.notificationService.error('Feil ved registrering av bytte');
        }
        throw error;
      }),
      finalize(() => {
        setState(patch({ isLoadingMobilBytteBytteRegistration: false }));
      })
    );
  }
}
